import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiURl from '../Paths/Api';

const CreateMeeting = () => {
  const [meetingData, setMeetingData] = useState({
    title: '',
    location: '',
    time: '',
    description: '',
    purpose: '',
    company: '',
    isPublic: 'private',
    capacity: '',
    price: '',
    image: null,
  });

  const [attendees, setAttendees] = useState([{ name: '', email: '', phone: '', role: '' }]);
  const [csvFile, setCsvFile] = useState(null);
  const [confirmPreview, setConfirmPreview] = useState(null);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [inputMode, setInputMode] = useState('manual');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [credits, setCredits] = useState(0);
  const [showCreditPopup, setShowCreditPopup] = useState(false);
  const [creditAmount, setCreditAmount] = useState('');
  const [isCreditAnimating, setIsCreditAnimating] = useState(false);
  const navigate = useNavigate();

  // Load Razorpay SDK dynamically
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Clear error/success messages after 5 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Fetch user data and credits from API
  useEffect(() => {
    const fetchUserCredits = async () => {
      try {
        const userId = localStorage.getItem('userId');
        const token = localStorage.getItem('token');
        const tokenExpiresAt = localStorage.getItem('token_expires_at');

        if (!userId || !token) {
          setError('User not authenticated. Please log in.');
          localStorage.clear();
          navigate('/');
          return;
        }

        if (tokenExpiresAt && new Date(tokenExpiresAt) < new Date()) {
          setError('Session expired. Please log in again.');
          localStorage.clear();
          navigate('/');
          return;
        }

        const response = await fetch(`${ApiURl}/getCredits.php?user_id=${userId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            setError('Session invalid. Please log in again.');
            localStorage.clear();
            navigate('/');
            return;
          }
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        if (data.success) {
          const fetchedCredits = parseInt(data.credits) || 0;
          setCredits(fetchedCredits);
          localStorage.setItem('credits', fetchedCredits);
        } else {
          throw new Error(data.message || 'Failed to fetch credits');
        }
      } catch (err) {
        setError(err.message || 'Failed to fetch credits. Please try again later.');
      }
    };

    const storedCompany = localStorage.getItem('company_name') || 'Your Company';
    setMeetingData((prev) => ({ ...prev, company: storedCompany }));
    fetchUserCredits();
  }, [navigate]);

  const handleMeetingChange = (e) => {
    const { name, value, files } = e.target;
    setMeetingData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  const handleAttendeeChange = (index, e) => {
    const { name, value } = e.target;
    const newAttendees = [...attendees];
    newAttendees[index][name] = value;
    setAttendees(newAttendees);
  };

  const addAttendee = () => {
    setAttendees([...attendees, { name: '', email: '', phone: '', role: '' }]);
  };

  const removeAttendee = (index) => {
    setAttendees(attendees.filter((_, i) => i !== index));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        setError('Please upload a valid CSV file.');
        return;
      }
      setCsvFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target.result;
        try {
          const rows = text.split('\n').map(row => row.split(',').map(cell => cell.trim())).filter(row => row.some(cell => cell));
          if (rows.length < 2) {
            setError('CSV file is empty or contains only headers.');
            setCsvFile(null);
            return;
          }
          const headers = rows[0].map(h => h.toLowerCase());
          const requiredHeaders = ['name', 'email', 'phone', 'role'];
          if (!requiredHeaders.every(h => headers.includes(h))) {
            setError('CSV must contain Name, Email, Phone, and Role columns');
            setCsvFile(null);
            return;
          }

          const validRoles = ['user', 'admin', 'supervisor'];
          const data = rows.slice(1).map(row => ({
            name: row[headers.indexOf('name')] || '',
            email: row[headers.indexOf('email')] || '',
            phone: row[headers.indexOf('phone')] || '',
            role: row[headers.indexOf('role')] ? row[headers.indexOf('role')].toLowerCase() : '',
          })).filter(attendee => attendee.name && attendee.email && attendee.phone && attendee.role && validRoles.includes(attendee.role));

          if (data.length === 0) {
            setError('No valid attendee data found in CSV. Ensure roles are "user", "admin", or "supervisor".');
            setCsvFile(null);
            return;
          }

          setConfirmPreview(data);
          setShowConfirmPopup(true);
        } catch (err) {
          setError('Error parsing CSV file. Please ensure it is formatted correctly.');
          setCsvFile(null);
        }
      };
      reader.onerror = () => {
        setError('Error reading CSV file.');
        setCsvFile(null);
      };
      reader.readAsText(file);
    }
  };

  const downloadTemplate = () => {
    const csvContent = "Name,Email,Phone,Role\nJohn Doe,john@example.com,1234567890,user\nJane Smith,jane@example.com,0987654321,admin\nBob Johnson,bob@example.com,9876543210,supervisor";
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'attendees_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  const handleAddCredits = async () => {
    const amount = parseInt(creditAmount);
    if (isNaN(amount) || amount < 100) {
      setError('Minimum credit purchase is ₹100 (100 credits)');
      return;
    }

    setIsSubmitting(true);
    try {
      const userId = localStorage.getItem('userId');
      const token = localStorage.getItem('token');

      if (!userId || !token) {
        setError('User not authenticated. Please log in.');
        localStorage.clear();
        navigate('/');
        return;
      }

      const orderResponse = await fetch(`${ApiURl}/createRazorpayOrder.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: userId,
          amount: amount * 100,
        }),
      });

      if (!orderResponse.ok) {
        if (orderResponse.status === 401) {
          setError('Session invalid. Please log in again.');
          localStorage.clear();
          navigate('/');
          return;
        }
        throw new Error(`HTTP error! Status: ${orderResponse.status}`);
      }

      const orderData = await orderResponse.json();
      if (!orderData.success) {
        setError(orderData.message || 'Failed to create payment order');
        return;
      }

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'YOUR_RAZORPAY_KEY_ID',
        amount: orderData.amount,
        currency: 'INR',
        name: 'Event Manager',
        description: `Purchase ${amount} credits`,
        order_id: orderData.order_id,
        handler: async (response) => {
          try {
            const verifyResponse = await fetch(`${ApiURl}/verifyRazorpayPayment.php`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({
                user_id: userId,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
                amount: amount,
              }),
            });

            if (!verifyResponse.ok) {
              if (verifyResponse.status === 401) {
                setError('Session invalid. Please log in again.');
                localStorage.clear();
                navigate('/');
                return;
              }
              throw new Error(`HTTP error! Status: ${verifyResponse.status}`);
            }

            const verifyData = await verifyResponse.json();
            if (verifyData.success) {
              setIsCreditAnimating(true);
              setTimeout(() => setIsCreditAnimating(false), 1000);
              setCredits(verifyData.new_credits);
              localStorage.setItem('credits', verifyData.new_credits);
              setCreditAmount('');
              setShowCreditPopup(false);
              setSuccess(`Added ${amount} credits successfully!`);
            } else {
              setError(verifyData.message || 'Payment verification failed');
            }
          } catch (err) {
            setError('An error occurred while verifying payment. Please try again.');
          }
        },
        prefill: {
          name: localStorage.getItem('full_name') || '',
          email: localStorage.getItem('email') || '',
          contact: localStorage.getItem('phone') || '',
        },
        theme: {
          color: '#0D9488',
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', (response) => {
        setError(`Payment failed: ${response.error.description}`);
      });
      razorpay.open();
    } catch (err) {
      setError('An error occurred while initiating payment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmSubmit = async () => {
    setShowConfirmPopup(false);
    await submitForm(confirmPreview, inputMode === 'csv');
  };

  const validateMeetingData = () => {
    const { title, location, time, description, purpose, isPublic, capacity, image } = meetingData;
    if (!title.trim() || !location.trim() || !time || !description.trim() || !purpose.trim()) {
      setError('All meeting details (Title, Location, Time, Description, Purpose) are required.');
      return false;
    }
    const meetingTime = new Date(time);
    if (meetingTime < new Date()) {
      setError('Meeting time cannot be in the past.');
      return false;
    }
    if (isPublic === 'public' && (!capacity || parseInt(capacity) <= 0)) {
      setError('Capacity must be a positive number for public meetings.');
      return false;
    }
    if (isPublic === 'public' && !image) {
      setError('An event image is required for public meetings.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!validateMeetingData()) return;

    let attendeesData = [];
    if (inputMode === 'csv') {
      if (csvFile && confirmPreview) {
        attendeesData = confirmPreview;
      } else {
        setError('No valid CSV data to submit.');
        return;
      }
    } else {
      attendeesData = attendees.filter(a => a.name && a.email && a.phone && a.role);
    }

    const tempInviteCount = attendeesData.length;
    if (tempInviteCount > credits) {
      setError(`Insufficient credits! You have ${credits} credits, but need ${tempInviteCount} for this meeting.`);
      return;
    }

    setConfirmPreview(attendeesData);
    setShowConfirmPopup(true);
  };

  const submitForm = async (attendeesData, isCsv = false) => {
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    const userDetails = {
      name: localStorage.getItem('full_name') || '',
      email: localStorage.getItem('email') || '',
      phone: localStorage.getItem('phone') || '',
      role: localStorage.getItem('role') || 'admin',
    };

    let updatedAttendees = attendeesData;
    let inviteCount = attendeesData.length;

    // For private meetings with no attendees, add the creator
    if (meetingData.isPublic !== 'public' && attendeesData.length === 0) {
      updatedAttendees = [userDetails];
      inviteCount = 1;
    }

    const formData = new FormData();
    formData.append('title', meetingData.title);
    formData.append('location', meetingData.location);
    formData.append('time', meetingData.time);
    formData.append('description', meetingData.description);
    formData.append('purpose', meetingData.purpose);
    formData.append('company', meetingData.company);
    formData.append('user_id', localStorage.getItem('userId'));
    formData.append('invite_count', inviteCount);
    formData.append('is_public', meetingData.isPublic === 'public' ? 1 : 0);
    formData.append('capacity', meetingData.isPublic === 'public' ? meetingData.capacity : 0);
    formData.append('price', meetingData.price || 0);
    if (meetingData.isPublic === 'public' && meetingData.image) {
      formData.append('image', meetingData.image);
    }

    // Send attendees to backend for both public and private meetings
    if (updatedAttendees.length > 0) {
      if (isCsv) {
        formData.append('attendees_csv', csvFile);
      } else {
        formData.append('attendees', JSON.stringify(updatedAttendees));
      }
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('User not authenticated. Please log in.');
        localStorage.clear();
        navigate('/');
        return;
      }

      const response = await fetch(`${ApiURl}/createMeeting.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('Session invalid. Please log in again.');
          localStorage.clear();
          navigate('/');
          return;
        }
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        if (inviteCount > 0) {
          setCredits(prev => {
            const newCredits = prev - inviteCount;
            localStorage.setItem('credits', newCredits);
            return newCredits;
          });
        }
        setSuccess('Meeting created successfully!');
        setMeetingData({ title: '', location: '', time: '', description: '', purpose: '', company: meetingData.company, isPublic: 'private', capacity: '', price: '', image: null });
        setAttendees([{ name: '', email: '', phone: '', role: '' }]);
        setCsvFile(null);
        setConfirmPreview(null);
        setInputMode('manual');
      } else {
        setError(data.message || 'Failed to create meeting');
      }
    } catch (err) {
      setError('An error occurred while creating the meeting. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 font-sans">
      <nav className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white p-3 shadow-lg fixed w-full z-20">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div className="flex items-center">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="mr-3 md:hidden focus:outline-none hover:bg-teal-600 p-2 rounded-full transition-all duration-300"
              aria-label="Toggle Sidebar"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <h1 className="text-lg font-bold tracking-tight">Event Manager</h1>
          </div>
          <button
            onClick={handleLogout}
            className="px-3 py-1.5 bg-red-500 rounded-full hover:bg-red-600 transition-all duration-300 text-sm font-medium shadow-sm hover:scale-105"
            aria-label="Logout"
          >
            Logout
          </button>
        </div>
      </nav>

      <div className="fixed top-16 right-4 z-20">
        <div
          className={`flex items-center bg-white shadow-md rounded-full px-3 py-1 cursor-pointer transition-all duration-300 ${isCreditAnimating ? 'animate-pulse' : 'hover:shadow-lg'}`}
          onClick={() => setShowCreditPopup(true)}
          role="button"
          aria-label={`Current credits: ${credits}`}
        >
          <span className="text-teal-600 font-medium text-sm mr-1">{credits}</span>
          <span className="text-xs text-gray-500 mr-1">credits</span>
          <svg className="w-4 h-4 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
        </div>
      </div>

{/* Sidebar */}
<div
  className={`fixed top-14 left-0 h-[calc(100vh-3.5rem)] bg-gradient-to-b from-teal-600 to-teal-800 text-white w-16 flex flex-col items-center py-4 space-y-4 transition-transform duration-300 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 md:w-16 z-20`}
>
  <button
    className="p-2 rounded-full bg-teal-700 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
    title="Dashboard"
    onClick={() => navigate('/dashboard')}
  >
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
    <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Dashboard</span>
  </button>
  <button
    className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
    title="Create Meeting"
    onClick={() => navigate('/create-meeting')}
  >
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
    </svg>
    <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Create Meeting</span>
  </button>
  <button
    className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
    title="View Meetings"
    onClick={() => navigate('/view-meetings')}
  >
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>
    <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Reports</span>
  </button>
  <button
    className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
    title="Send Invites"
    onClick={() => navigate('/send-invites')}
  >
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7-4 7 4v8l-7 4-7-4V8zm0 0l7 4 7-4m-14 0v8l7 4 7-4V8" />
    </svg>
    <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Invite</span>
  </button>
  <button
    className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
    title="Search Meetings"
    onClick={() => navigate('/search-form')}
  >
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
    <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Search</span>
  </button>
  <button
    className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
    title="Send Invite Without Pay"
    onClick={() => navigate('/adminsend')}
  >
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7-4 7 4v8l-7 4-7-4V8zm0 0l7 4 7-4m-14 0v8l7 4 7-4V8" />
    </svg>
    <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Send Invite</span>
  </button>
  <button
    className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
    title="Restrict Meeting"
    onClick={() => navigate('/restrict-meeting')}
  >
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 1.104-.896 2-2 2s-2-.896-2-2 2-4 2-4 2 2.896 2 4zm0 0c0 1.104.896 2 2 2s2-.896 2-2-2-4-2-4-2 2.896-2 4zm-6 7c0 1.104.896 2 2 2s2-.896 2-2-2-4-2-4-2 2.896-2 4zm12 0c0 1.104.896 2 2 2s2-.896 2-2-2-4-2-4-2 2.896-2 4z"/>
    </svg>
    <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Restrict Meeting</span>
  </button>
</div>

      <div className="pt-20 md:pl-20 p-4 max-w-4xl mx-auto transition-all duration-500">
        <h1 className="text-xl font-bold text-gray-900 mb-4 animate-fade-in">Create New Meeting</h1>
        <div className="fixed top-24 right-4 bg-teal-100 text-teal-800 p-2 rounded-lg text-xs shadow-md sm:text-sm">
          {meetingData.isPublic === 'public'
            ? 'Public meeting is visible to everyone. Invitations are optional.'
            : 'Private meeting requires at least one invited attendee.'}
        </div>
        {error && (
          <div className="mb-3 p-3 bg-red-50 text-red-600 rounded-lg border border-red-100 text-sm animate-fade-in">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-3 p-3 bg-green-50 text-green-600 rounded-lg border border-red-100 text-sm animate-fade-in">
            {success}
          </div>
        )}
        <form onSubmit={handleSubmit} className="bg-white p-5 rounded-xl shadow-lg space-y-4 animate-fade-in">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700" htmlFor="title">Event Title <span className="text-red-500">*</span></label>
              <input
                id="title"
                type="text"
                name="title"
                value={meetingData.title}
                onChange={handleMeetingChange}
                className="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-300 hover:bg-gray-50"
                required
                aria-required="true"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700" htmlFor="company">Company Name <span className="text-red-500">*</span></label>
              <input
                id="company"
                type="text"
                name="company"
                value={meetingData.company}
                onChange={handleMeetingChange}
                className="w-full px-3 py-1.5 bg-gray-100 border border-gray-300 rounded-lg text-sm cursor-not-allowed transition-all duration-300"
                readOnly
                required
                aria-readonly="true"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700" htmlFor="location">Location <span className="text-red-500">*</span></label>
              <input
                id="location"
                type="text"
                name="location"
                value={meetingData.location}
                onChange={handleMeetingChange}
                className="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-300 hover:bg-gray-50"
                required
                aria-required="true"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700" htmlFor="time">Time <span className="text-red-500">*</span></label>
              <input
                id="time"
                type="datetime-local"
                name="time"
                value={meetingData.time}
                onChange={handleMeetingChange}
                className="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-300 hover:bg-gray-50"
                required
                aria-required="true"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700" htmlFor="purpose">Purpose <span className="text-red-500">*</span></label>
              <input
                id="purpose"
                type="text"
                name="purpose"
                value={meetingData.purpose}
                onChange={handleMeetingChange}
                className="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-300 hover:bg-gray-50"
                required
                aria-required="true"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700" htmlFor="isPublic">Meeting Type <span className="text-red-500">*</span></label>
              <select
                id="isPublic"
                name="isPublic"
                value={meetingData.isPublic}
                onChange={handleMeetingChange}
                className="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-300 hover:bg-gray-50"
                required
                aria-required="true"
              >
                <option value="private">Private</option>
                <option value="public">Public</option>
              </select>
            </div>
            {meetingData.isPublic === 'public' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-700" htmlFor="capacity">Capacity <span className="text-red-500">*</span></label>
                  <input
                    id="capacity"
                    type="number"
                    name="capacity"
                    value={meetingData.capacity}
                    onChange={handleMeetingChange}
                    className="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-300 hover:bg-gray-50"
                    min="1"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700" htmlFor="price">Ticket Price (₹)</label>
                  <input
                    id="price"
                    type="number"
                    name="price"
                    value={meetingData.price}
                    onChange={handleMeetingChange}
                    className="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-300 hover:bg-gray-50"
                    min="0"
                    step="0.01"
                    placeholder="Enter ticket price (optional)"
                    aria-label="Ticket Price"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700" htmlFor="image">Event Image <span className="text-red-500">*</span></label>
                  <input
                    id="image"
                    type="file"
                    name="image"
                    accept="image/*"
                    onChange={handleMeetingChange}
                    className="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm transition-all duration-300 hover:bg-gray-50"
                    required
                    aria-required="true"
                  />
                </div>
              </>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700" htmlFor="description">Description <span className="text-red-500">*</span></label>
            <textarea
              id="description"
              name="description"
              value={meetingData.description}
              onChange={handleMeetingChange}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-300 hover:bg-gray-50"
              rows="2"
              required
              aria-required="true"
            />
          </div>

          <div className="flex space-x-3">
            <button
              type="button"
              onClick={() => setInputMode('manual')}
              className={`flex-1 py-1.5 px-3 rounded-lg text-sm font-medium transition-all duration-300 transform hover:scale-105 ${inputMode === 'manual' ? 'bg-teal-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              aria-label="Switch to Manual Input"
            >
              Manual Input
            </button>
            <button
              type="button"
              onClick={() => setInputMode('csv')}
              className={`flex-1 py-1.5 px-3 rounded-lg text-sm font-medium transition-all duration-300 transform hover:scale-105 ${inputMode === 'csv' ? 'bg-teal-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              aria-label="Switch to CSV Upload"
            >
              CSV Upload
            </button>
          </div>

          {(inputMode === 'manual' || meetingData.isPublic === 'public') && (
            <div className="animate-fade-in">
              <h2 className="text-sm font-semibold text-gray-600 mb-3">Add Attendees (Optional)</h2>
              {attendees.map((attendee, index) => (
                <div key={index} className="space-y-2 border p-3 rounded-lg mb-3 bg-gray-50 transition-all duration-300 hover:shadow-md">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600" htmlFor={`attendee-name-${index}`}>Name</label>
                      <input
                        id={`attendee-name-${index}`}
                        type="text"
                        name="name"
                        value={attendee.name}
                        onChange={(e) => handleAttendeeChange(index, e)}
                        className="w-full px-2 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-200 hover:bg-gray-50"
                        aria-label={`Attendee ${index + 1} Name`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600" htmlFor={`attendee-email-${index}`}>Email</label>
                      <input
                        id={`attendee-email-${index}`}
                        type="email"
                        name="email"
                        value={attendee.email}
                        onChange={(e) => handleAttendeeChange(index, e)}
                        className="w-full px-2 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-200 hover:bg-gray-50"
                        aria-label={`Attendee ${index + 1} Email`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600" htmlFor={`attendee-phone-${index}`}>Phone</label>
                      <input
                        id={`attendee-phone-${index}`}
                        type="tel"
                        name="phone"
                        value={attendee.phone}
                        onChange={(e) => handleAttendeeChange(index, e)}
                        className="w-full px-2 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-200 hover:bg-gray-50"
                        aria-label={`Attendee ${index + 1} Phone`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600" htmlFor={`attendee-role-${index}`}>Role</label>
                      <select
                        id={`attendee-role-${index}`}
                        name="role"
                        value={attendee.role}
                        onChange={(e) => handleAttendeeChange(index, e)}
                        className="w-full px-2 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-200 hover:bg-gray-50"
                        aria-label={`Attendee ${index + 1} Role`}
                      >
                        <option value="">Select Role</option>
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                        <option value="supervisor">Supervisor</option>
                      </select>
                    </div>
                  </div>
                  {index > 0 && (
                    <button
                      type="button"
                      onClick={() => removeAttendee(index)}
                      className="mt-2 px-2 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all duration-200 text-xs sm:text-sm transform hover:shadow-sm"
                      aria-label={`Remove Attendee ${index + 1}`}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addAttendee}
                className="px-3 py-1.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-all duration-200 text-sm transform hover:shadow-sm"
                aria-label="Add New Attendee"
              >
                Add Attendee
              </button>
            </div>
          )}

          {inputMode === 'csv' && (
            <div className="border-t pt-3 animate-fade-in">
              <h2 className="text-sm font-semibold text-gray-600 mb-2">Upload Attendees via CSV (Optional)</h2>
              <p className="text-gray-600 text-xs mb-2">Download the template, fill it with Name, Email, Phone, and Role (user, admin, supervisor), then upload.</p>
              <button
                type="button"
                onClick={downloadTemplate}
                className="px-3 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all duration-200 text-sm transform hover:shadow-sm"
                aria-label="Download CSV Template"
              >
                Download CSV Template
              </button>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="mt-3 block w-full text-xs sm:text-sm text-gray-600 bg-white border border-gray-300 rounded-lg file:mr-4 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs sm:file:text-sm file:font-medium file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100 transition-all duration-200"
                aria-label="Upload CSV File"
              />
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-2 px-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-medium rounded-lg hover:from-teal-600 hover:to-cyan-600 transition-all duration-200 shadow-sm text-sm sm:text-base ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-md'
              }`}
            aria-label={isSubmitting ? 'Creating Meeting...' : 'Create Meeting'}
          >
            {isSubmitting ? 'Creating Meeting...' : 'Create Meeting'}
          </button>
        </form>
      </div>

      {showConfirmPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-white p-5 rounded-xl shadow-lg max-w-2xl w-full max-h-[70vh] overflow-y-auto transform scale-95 animate-scale-in">
            <h2 className="text-sm font-bold mb-3 text-gray-800">Confirm Meeting and Attendees</h2>
            <div className="mb-4">
              <h3 className="text-xs font-semibold mb-2 text-gray-700">Meeting Details</h3>
              <ul className="list-disc pl-4 text-xs text-gray-600">
                <li><strong>Title:</strong> {meetingData.title}</li>
                <li><strong>Location:</strong> {meetingData.location}</li>
                <li><strong>Time:</strong> {new Date(meetingData.time).toLocaleString()}</li>
                <li><strong>Description:</strong> {meetingData.description}</li>
                <li><strong>Purpose:</strong> {meetingData.purpose}</li>
                <li><strong>Company:</strong> {meetingData.company}</li>
                <li><strong>Type:</strong> {meetingData.isPublic === 'public' ? 'Public' : 'Private'}</li>
                {meetingData.isPublic === 'public' && (
                  <>
                    <li><strong>Capacity:</strong> {meetingData.capacity}</li>
                    <li><strong>Price:</strong> {meetingData.price || 'Free'}</li>
                    <li><strong>Image:</strong> {meetingData.image ? meetingData.image.name : 'No image'}</li>
                  </>
                )}
              </ul>
            </div>
            <h3 className="text-xs font-semibold mb-2 text-gray-700">Attendees</h3>
            {confirmPreview && confirmPreview.length > 0 ? (
              <table className="w-full border-collapse text-xs sm:text-sm">
                <thead>
                  <tr className="bg-teal-50">
                    <th className="border border-gray-200 p-2 text-left font-medium text-gray-700">Name</th>
                    <th className="border border-gray-200 p-2 text-left font-medium text-gray-700">Email</th>
                    <th className="border border-gray-200 p-2 text-left font-medium text-gray-700">Phone</th>
                    <th className="border border-gray-200 p-2 text-left font-medium text-gray-700">Role</th>
                  </tr>
                </thead>
                <tbody>
                  {confirmPreview.map((attendee, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-all duration-200">
                      <td className="border border-gray-200 p-2 text-gray-600">{attendee.name}</td>
                      <td className="border border-gray-200 p-2 text-gray-600">{attendee.email}</td>
                      <td className="border border-gray-200 p-2 text-gray-600">{attendee.phone}</td>
                      <td className="border border-gray-200 p-2 text-gray-600">{attendee.role}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-xs sm:text-sm text-gray-600">
                No attendees. {meetingData.isPublic === 'public' ? '' : 'Creator will be added automatically.'}
              </p>
            )}
            <div className="mt-3 flex justify-end space-x-2">
              <button
                onClick={() => {
                  setShowConfirmPopup(false);
                  setCsvFile(null);
                  setConfirmPreview(null);
                }}
                className="px-3 py-1.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all duration-200 text-xs sm:text-sm transform hover:shadow-sm"
                aria-label="Cancel"
              >
                Cancel
              </button>
              <button
                onClick={confirmSubmit}
                className="px-3 py-1.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-all duration-200 text-xs sm:text-sm transform hover:shadow-sm"
                aria-label="Confirm and Submit"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreditPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-white p-5 rounded-lg shadow-lg max-w-md w-full transform scale-95 animate-scale-in">
            <h2 className="text-sm font-semibold mb-2 text-gray-800">Add Credits</h2>
            <p className="text-xs text-gray-500 mb-3">Minimum purchase: ₹100 (100 credits). 1 credit = 1 invite.</p>
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="current-credits">
                Current Credits
              </label>
              <div
                id="current-credits"
                className="px-3 py-1.5 bg-gray-100 rounded-lg text-sm font-medium text-gray-600"
              >
                {credits} credits
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="credit-amount">
                Amount to Add (₹)
              </label>
              <input
                id="credit-amount"
                type="number"
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
                className="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-200 hover:bg-gray-50"
                min="100"
                required
                aria-required="true"
                aria-label="Amount to Add in Rupees"
              />
            </div>
            <div className="mt-3 flex justify-end space-x-2">
              <button
                onClick={() => {
                  setShowCreditPopup(false);
                  setCreditAmount('');
                }}
                className="px-3 py-1.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all duration-200 text-xs sm:text-sm transform hover:shadow-sm"
                aria-label="Cancel Add Credits"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCredits}
                disabled={isSubmitting}
                className={`px-3 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all duration-200 text-xs sm:text-sm transform hover:shadow-sm ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                aria-label={isSubmitting ? 'Processing Credit Purchase' : 'Proceed to Payment'}
              >
                {isSubmitting ? 'Processing...' : 'Proceed to Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateMeeting;